import React, { useCallback, useContext, useRef, useMemo } from 'react';
import type { ComponentType, FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { FlagsProvider } from '@atlaskit/flag/flag-provider';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import WelcomeFeedIcon from '@atlaskit/icon-lab/core/welcome-feed';
import SunsetIcon from '@atlaskit/icon-lab/core/sunset';
import VideoIcon from '@atlaskit/icon/core/video';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics/ai-events-instrumentation-provider';
import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics/ai-events-instrumentation-context';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { keys as sessionStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { FEED_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ActivityFilterDialog } from '@confluence/activity-filter/entry-points/ActivityFilterDialog.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';

import { FeedHeader } from './FeedHeader/index.ts';
import { FeedSnippetsFeedbackComponent } from './FeedSnippetsFeedbackComponent.tsx';
import { ActivityFeedCards, PopularFeedCards } from './FeedCards/index.ts';
import { useFeedRenderedTrackEvent } from './useFeedRenderedTrackEvent.ts';
import { useActivityFilterModalState } from './useActivityFilterModalState.ts';
import {
	useFeedTypeSelector,
	useAnonymousFeedTypeSelector,
	useFeedTypeOverride,
	FeedType,
} from './useFeedTypeSelector.ts';
import { GenericFeedError } from './FeedError/index.ts';
import { SimpleWrapper } from './FeedCards/FeedCard/FeedCard.tsx';
import { HOME_OVERVIEW_FEED_PAGE_SEGMENT } from './perf.config.ts';
import { FeedContext } from './FeedContext.ts';
import { isVisibleFollowingFeedNode, isVisiblePopularFeedNode } from './feedVisibility.ts';
import { useFeedSession } from './useFeedSession.ts';
import { FilterByLabelFeed } from './FilterByLabelFeed.tsx';
import { useFollowingFeedWithSortByStorage } from './useFollowingFeedWithSortByStorage.ts';
import { usePopularFeedWithTimeGranularityStorage } from './usePopularFeedWithTimeGranularityStorage.ts';
import { LoadableMeetingsPanel } from './MeetingsPanel/LoadableMeetingsPanel.ts';
import { useMeetingsVisibility } from './MeetingsPanel/useMeetings.ts';

const i18n = defineMessages({
	welcomeFeedLabel: {
		id: 'feed.welcome-label',
		defaultMessage: 'Welcome',
		description:
			'The text is shown as a label or heading for the Welcome state of the feed, displayed when the user first arrives or is introduced to the feed experience.',
	},
	farewellFeedLabel: {
		id: 'feed.farewell-label',
		defaultMessage: 'Farewell',
		description:
			'The text is shown as a label or heading for the Farewell state of the feed, displayed when the user is leaving or has completed their session with the feed.',
	},
	whatsNewInConfluenceFeedLabel: {
		id: 'feed.whatsNew-label',
		defaultMessage: 'What’s new in Confluence',
		description: 'Label Text for the Whats New in Conflunece Feed',
	},
	meetingsFeedLabel: {
		id: 'feed.meetings-label',
		defaultMessage: 'Meetings',
		description:
			'Label for the pill that switches the feed to show the user’s Loom meeting recordings and their action items.',
	},
});

export type CustomPanel = {
	title: string;
	key: string;
	// Accepts both a plain icon component and a lazily-loaded icon (e.g. `LoadableType` from
	// `@confluence/loadable`), since `LoadableType` is itself a `ComponentType`.
	icon?: ComponentType<NewCoreIconProps>;
	content: JSX.Element;
	renderWithoutWrapper?: boolean;
	ref?: React.RefObject<HTMLButtonElement>;
	isPulseActive?: boolean;
	onPulseClick?: () => void;
};

export enum PopularFeedTimeGranularityValue {
	Day = 'day',
	Default = 'default', // default is 2 weeks. cc-analytics expects "default" as a parameter for 2 weeks
	Month = 'month',
	Week = 'week',
	Year = 'year',
}

export enum FollowingFeedSortByValue {
	Relevant = 'relevant',
	Recent = 'chronological',
}

type FeedComponentProps = {
	renderPopularFeed: boolean;
	customPanels?: CustomPanel[];
};

const PANELS_WITHOUT_WRAPPER = ['confluence.home.welcome', 'confluence.home.farewell'];

const shouldRenderWithoutWrapper = (panel: CustomPanel) =>
	panel.renderWithoutWrapper || PANELS_WITHOUT_WRAPPER.includes(panel.key);

type ConditionalWrapperProps = {
	condition: boolean;
	WrapperIfTrue: FC<{ children: React.ReactNode }>;
	WrapperIfFalse: FC<{ children: React.ReactNode }>;
	children: React.ReactNode;
};

const NoWrapper: FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

const CustomPanelWrapper: FC<{ children: React.ReactNode }> = ({ children }) => (
	<SimpleWrapper role="none" data-testid="custom-panel-wrapper">
		{children}
	</SimpleWrapper>
);

const ConditionalWrapper: FC<ConditionalWrapperProps> = ({
	condition,
	WrapperIfTrue,
	WrapperIfFalse,
	children,
}) =>
	condition ? (
		<WrapperIfTrue>{children}</WrapperIfTrue>
	) : (
		<WrapperIfFalse>{children}</WrapperIfFalse>
	);

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'feed',
	aiFeatureName: 'aiSnippetsInFeeds',
	proactiveGeneratedAI: 0,
	userGeneratedAI: 0,
};

const FeedComponent: FC<FeedComponentProps> = ({ renderPopularFeed, customPanels }) => {
	const { isLoggedIn } = useSessionData();
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { feedTypeOverride, clearFeedTypeOverride } = useFeedTypeOverride();
	const loggedInFeedTypeSelector = useFeedTypeSelector();
	const anonymousFeedTypeSelector = useAnonymousFeedTypeSelector();
	const popularFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const followingFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const welcomeFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const farewellFeedPillRef = useRef<HTMLButtonElement | null>(null);
	const meetingsFeedPillRef = useRef<HTMLButtonElement | null>(null);

	// `shouldShow` is true only for gate-on + eligible + treatment users, so
	// control/ineligible users never see the pill. Query is skipped when gate off.
	const { shouldShow: shouldShowMeetingsPill } = useMeetingsVisibility({
		enabled: fg('meetings-tab-confluence-for-you-gate'),
	});

	const handleFocusRef = (feedPillRef: React.RefObject<HTMLButtonElement>) => {
		const feedPillRefButton = feedPillRef.current;
		if (feedPillRefButton) {
			feedPillRefButton.focus();
		}
	};

	const {
		loading: loadingPreferences,
		queryError,
		mutationError,
		feedType,
		updateFeedType,
		hasSelectedFeedTypePreference,
	} = isLoggedIn ? loggedInFeedTypeSelector : anonymousFeedTypeSelector;

	const filterOrOverride = feedTypeOverride || feedType;

	const updateFeedTypeAndClearOverride = useCallback(
		(updatedFeedType: FeedType | string) => {
			clearFeedTypeOverride();
			updateFeedType(updatedFeedType);
		},
		[updateFeedType, clearFeedTypeOverride],
	);

	const handleFeedSelectorChange = useCallback(
		(updatedFeedType: FeedType | string) => {
			experienceTracker.start({
				name: FEED_EXPERIENCE,
				attributes: { feedTypeChange: true },
			});
			updateFeedTypeAndClearOverride(updatedFeedType);
		},
		[experienceTracker, updateFeedTypeAndClearOverride],
	);

	const {
		followingFeedSortBy,
		setFollowingFeedSortBy,
		followingFeedQuery: feedContentQuery,
	} = useFollowingFeedWithSortByStorage();

	const hasFollowedAnyone =
		(feedContentQuery?.data?.getFeedUserConfig?.users?.length || 0) > 0 ||
		(feedContentQuery?.data?.getFeedUserConfig?.spaces?.length || 0) > 0;

	if (feedContentQuery?.error) {
		if (isExpectedApolloError(feedContentQuery.error)) {
			markErrorAsHandled(feedContentQuery.error);
		}
	}

	const refetchFeedContentQuery = feedContentQuery.refetch;
	const handleActivityFilterModalSave = useCallback(() => {
		void refetchFeedContentQuery?.({ sortBy: followingFeedSortBy });
		handleFeedSelectorChange(FeedType.FOLLOWING);
	}, [refetchFeedContentQuery, followingFeedSortBy, handleFeedSelectorChange]);

	const handleFollowingFeedSortByDropdownChange = useCallback(
		(sortBy: FollowingFeedSortByValue) => {
			setFollowingFeedSortBy(sortBy);
			void refetchFeedContentQuery?.({ sortBy });
		},
		[refetchFeedContentQuery, setFollowingFeedSortBy],
	);

	const { popularFeedTimeGranularity, setPopularFeedTimeGranularity, popularFeedQuery } =
		usePopularFeedWithTimeGranularityStorage(renderPopularFeed);
	const refetchPopularFeedQuery = popularFeedQuery.refetch;
	const handlePopularFeedTimeGranularityDropdownChange = useCallback(
		(timeGranularity: PopularFeedTimeGranularityValue) => {
			setPopularFeedTimeGranularity(timeGranularity);
			void refetchPopularFeedQuery?.({ timeGranularity });
		},
		[refetchPopularFeedQuery, setPopularFeedTimeGranularity],
	);

	useFeedRenderedTrackEvent(filterOrOverride, feedContentQuery.data, popularFeedQuery.data);

	const followFeedSessionId = useFeedSession(
		feedContentQuery,
		sessionStorageKeys.HOME_FOLLOWING_FEED_SESSION_ID,
	);
	const followingFeedContext = useMemo(() => {
		return { feedSessionId: followFeedSessionId };
	}, [followFeedSessionId]);
	const popularFeedSessionId = useFeedSession(
		popularFeedQuery,
		sessionStorageKeys.HOME_POPULAR_FEED_SESSION_ID,
	);
	const popularFeedContext = useMemo(() => {
		return { feedSessionId: popularFeedSessionId };
	}, [popularFeedSessionId]);

	const { activityFilterModalOpen, openActivityFilterModal, closeActivityFilterModal } =
		useActivityFilterModalState();

	const welcomeAndFarewellPanels: CustomPanel[] = [
		{
			title: intl.formatMessage(i18n.welcomeFeedLabel),
			key: 'confluence.home.welcome',
			icon: WelcomeFeedIcon,
			content: (
				<FilterByLabelFeed
					handleFocusRef={() => handleFocusRef(welcomeFeedPillRef)}
					filterByLabel="welcome-blog"
				/>
			),
			ref: welcomeFeedPillRef,
		},
		{
			title: intl.formatMessage(i18n.farewellFeedLabel),
			key: 'confluence.home.farewell',
			icon: SunsetIcon,
			content: (
				<FilterByLabelFeed
					handleFocusRef={() => handleFocusRef(farewellFeedPillRef)}
					filterByLabel="farewell-blog"
				/>
			),
			ref: farewellFeedPillRef,
		},
	];

	const meetingsPanel: CustomPanel = {
		title: intl.formatMessage(i18n.meetingsFeedLabel),
		key: 'confluence.home.meetings',
		icon: VideoIcon,
		content: <LoadableMeetingsPanel />,
		ref: meetingsFeedPillRef,
		// Each meeting renders as its own bordered card, so opt out of the shared
		// CustomPanelWrapper to avoid a redundant border around the whole list.
		renderWithoutWrapper: true,
	};

	const updatedPanels = [
		...(fg('confluence_welcome_farewell_feed') ||
		StatsigConfigurations.getBoolean('confluence-welcome-farewell-feed-internal', false)
			? welcomeAndFarewellPanels
			: []),
		...(shouldShowMeetingsPill ? [meetingsPanel] : []),
		...(customPanels || []),
	];

	const renderPanel = (panel: CustomPanel) => {
		return (
			<Box
				role="tabpanel"
				id={`tabpanel-${panel.key}`}
				aria-labelledby={`tab-${panel.key}`}
				tabIndex={0}
			>
				<>{panel.content}</>
				<ExperienceSuccess //if we render an external panel we'll consider it a success, as the data is fetched externally
					name={FEED_EXPERIENCE}
					key={panel.key}
					attributes={{ feedType: panel.key }}
				/>
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
			</Box>
		);
	};

	if (loadingPreferences) {
		return null;
	}

	const renderFeedCards = () => {
		if (
			updatedPanels &&
			!!updatedPanels.length &&
			!(Object.values(FeedType) as string[]).includes(filterOrOverride) // ensures the custom panel isn't just one of the expected panels
		) {
			const panel = updatedPanels.find((element) => element.key === filterOrOverride);

			if (panel?.content) {
				return (
					<ConditionalWrapper
						condition={shouldRenderWithoutWrapper(panel)}
						WrapperIfTrue={NoWrapper}
						WrapperIfFalse={CustomPanelWrapper}
					>
						{renderPanel(panel)}
					</ConditionalWrapper>
				);
			}
		}

		if (!hasSelectedFeedTypePreference) {
			if (feedContentQuery?.data?.feed?.nodes.length) {
				return (
					<FeedContext.Provider value={followingFeedContext}>
						<ActivityFeedCards
							feedContentQuery={feedContentQuery}
							openActivityFilterModal={openActivityFilterModal}
							handleFocusRef={() => handleFocusRef(followingFeedPillRef)}
							followingFeedSortBy={followingFeedSortBy}
							hasFollowedAnyone={hasFollowedAnyone}
						/>
					</FeedContext.Provider>
				);
			} else if (popularFeedQuery?.data?.popularFeed?.nodes.length) {
				return (
					<FeedContext.Provider value={popularFeedContext}>
						<PopularFeedCards
							popularFeedQuery={popularFeedQuery}
							handleFocusRef={() => handleFocusRef(popularFeedPillRef)}
							popularFeedTimeGranularity={popularFeedTimeGranularity}
						/>
					</FeedContext.Provider>
				);
			}
		}

		if (filterOrOverride === FeedType.POPULAR) {
			return (
				<FeedContext.Provider value={popularFeedContext}>
					<PopularFeedCards
						popularFeedQuery={popularFeedQuery}
						handleFocusRef={() => handleFocusRef(popularFeedPillRef)}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
					/>
				</FeedContext.Provider>
			);
		}

		return (
			<FeedContext.Provider value={followingFeedContext}>
				<ActivityFeedCards
					feedContentQuery={feedContentQuery}
					openActivityFilterModal={openActivityFilterModal}
					handleFocusRef={() => handleFocusRef(followingFeedPillRef)}
					followingFeedSortBy={followingFeedSortBy}
					hasFollowedAnyone={hasFollowedAnyone}
				/>
			</FeedContext.Provider>
		);
	};

	const isFollowingOrPopularFeed =
		filterOrOverride === FeedType.FOLLOWING || filterOrOverride === FeedType.POPULAR;
	const followingFeedHasVisibleResults = Boolean(
		feedContentQuery?.data?.feed?.nodes?.some(isVisibleFollowingFeedNode),
	);
	const popularFeedHasVisibleResults = Boolean(
		popularFeedQuery?.data?.popularFeed?.nodes?.some(isVisiblePopularFeedNode),
	);

	const isFollowingEmptyStateBanner =
		filterOrOverride === FeedType.FOLLOWING &&
		Boolean(feedContentQuery?.data) &&
		!followingFeedHasVisibleResults;
	const isPopularEmptyStateBanner =
		filterOrOverride === FeedType.POPULAR &&
		Boolean(popularFeedQuery?.data) &&
		!popularFeedHasVisibleResults;
	const isFeedEmptyStateBanner =
		expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true) &&
		(isFollowingEmptyStateBanner || isPopularEmptyStateBanner);

	// Hide the "Summaries use AI. Verify results." row when the Popular or Following empty-state banner
	// is shown (Popular tab OR Following tab + no visible results + experiment on)
	const shouldRenderSnippetsFeedback =
		isFollowingOrPopularFeed &&
		expValEquals('confluence_ai_snippets_in_following_popular_feeds', 'isEnabled', true) &&
		!isFeedEmptyStateBanner;

	return (
		<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
			<>
				<div data-testid="activity-feed">
					<FeedHeader
						openActivityFilterModal={openActivityFilterModal}
						renderPopularFeed={renderPopularFeed}
						feedType={filterOrOverride}
						setFeedType={handleFeedSelectorChange}
						customPanels={updatedPanels}
						popularFeedPillRef={popularFeedPillRef}
						followingFeedPillRef={followingFeedPillRef}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
						handlePopularFeedTimeGranularityDropdownChange={
							handlePopularFeedTimeGranularityDropdownChange
						}
						followingFeedSortBy={followingFeedSortBy}
						handleFollowingFeedSortByDropdownChange={handleFollowingFeedSortByDropdownChange}
						hasFollowedAnyone={hasFollowedAnyone}
					/>
					<ErrorBoundary attribution={Attribution.SMARTS}>
						{shouldRenderSnippetsFeedback ? <FeedSnippetsFeedbackComponent /> : null}
						{renderFeedCards()}
						<ModalTransition>
							{activityFilterModalOpen && (
								<ActivityFilterDialog
									onChanged={handleActivityFilterModalSave}
									onClose={closeActivityFilterModal}
								/>
							)}
						</ModalTransition>
						{queryError && <ErrorDisplay error={queryError} />}
						{mutationError && <ErrorDisplay error={mutationError} />}
					</ErrorBoundary>
				</div>
			</>
		</AIEventsInstrumentationProvider>
	);
};

type FeedProps = {
	renderPopularFeed?: boolean;
	customPanels?: CustomPanel[];
};
export const Feed: FC<FeedProps> = ({ renderPopularFeed = false, customPanels }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const failFeedExperience = useCallback(
		(error: Error) => {
			experienceTracker.fail({
				name: FEED_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);

	return (
		<FlagsProvider>
			<GenericErrorBoundary
				attribution={Attribution.SMARTS}
				renderOnError={() => <GenericFeedError />}
				onError={failFeedExperience}
			>
				<FeedComponent renderPopularFeed={renderPopularFeed} customPanels={customPanels} />
			</GenericErrorBoundary>
		</FlagsProvider>
	);
};
